import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sprite, SparkleBurst, CharacterView, CheckIcon } from "./fx";

// TapSequence — tap glowing footstep/paw markers IN ORDER. A dashed trail
// sweeps in, an escort walks each step, and the goal character waits at the
// end. Optional traffic-light gate (red -> green after ~2s). Out-of-order tap
// wiggles, no penalty. Done when all steps are tapped.

const buildPath = (points) => {
  if (!points.length) return "";
  const P = points.map((p) => ({ x: p.x * 100, y: p.y * 100 }));
  let d = `M ${P[0].x} ${P[0].y}`;
  for (let i = 1; i < P.length - 1; i += 1) {
    const mid = { x: (P[i].x + P[i + 1].x) / 2, y: (P[i].y + P[i + 1].y) / 2 };
    d += ` Q ${P[i].x} ${P[i].y} ${mid.x} ${mid.y}`;
  }
  d += ` L ${P[P.length - 1].x} ${P[P.length - 1].y}`;
  return d;
};

const stepAngle = (points, index) => {
  const from = points[Math.max(index - 1, 0)];
  const to = points[Math.min(index + 1, points.length - 1)];
  return (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI + 90;
};

const TapSequence = ({ payload, sounds, onDone }) => {
  const { stepPoints, goalPoint } = payload;
  const allPoints = [...stepPoints, goalPoint];
  const stageRef = useRef(null);
  const doneRef = useRef(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightGreen, setLightGreen] = useState(false);
  const [wiggle, setWiggle] = useState(null);
  const [burst, setBurst] = useState({ key: 0, left: "0%", top: "0%" });

  const isComplete = currentIndex >= stepPoints.length;
  const stepsUnlocked = !payload.waitForGreenLight || lightGreen;

  useEffect(() => {
    if (!payload.waitForGreenLight) return undefined;
    const id = window.setTimeout(() => {
      setLightGreen(true);
      sounds.playPop(0.7);
      sounds.speak("Green light — go!");
    }, 2100);
    return () => window.clearTimeout(id);
  }, [payload.waitForGreenLight, sounds]);

  const tap = useCallback(
    (index) => {
      if (!stepsUnlocked || isComplete) return;
      if (index !== currentIndex) {
        setWiggle(`${index}-${Date.now()}`);
        return;
      }
      sounds.playPop((currentIndex + 1) / stepPoints.length);
      sounds.playSparkle();
      const p = stepPoints[index];
      setBurst((b) => ({ key: b.key + 1, left: `${p.x * 100}%`, top: `${p.y * 100}%` }));
      const next = currentIndex + 1;
      setCurrentIndex(next);
      if (next >= stepPoints.length && !doneRef.current) {
        doneRef.current = true;
        window.setTimeout(() => onDone(), 1300);
      }
    },
    [currentIndex, isComplete, onDone, sounds, stepPoints, stepsUnlocked]
  );

  // Escort position (unit coords).
  let escortUnit;
  if (isComplete) escortUnit = goalPoint;
  else if (currentIndex === 0)
    escortUnit = { x: Math.max(0.08, stepPoints[0].x - 0.14), y: Math.min(0.92, stepPoints[0].y + 0.05) };
  else escortUnit = stepPoints[currentIndex - 1];

  return (
    <div className="ch-activity-body ch-tap">
      <div className="ch-stage" ref={stageRef}>
        <svg className="ch-trail" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <motion.path
            d={buildPath(allPoints)}
            fill="none"
            stroke="rgba(255,255,255,0.8)"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeDasharray="0.4 4"
            initial={{ opacity: 0, pathLength: 0 }}
            animate={{ opacity: 1, pathLength: 1 }}
            transition={{ duration: 1.4, delay: 0.3, ease: "easeInOut" }}
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {stepPoints.map((p, index) => {
          const isDone = index < currentIndex;
          const isCurrent = index === currentIndex && stepsUnlocked;
          return (
            <motion.div
              key={index}
              className={`ch-step ${isDone ? "is-done" : ""} ${isCurrent ? "is-current" : ""}`}
              style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
              animate={wiggle && wiggle.startsWith(`${index}-`) ? { x: [0, -6, 6, -4, 4, 0] } : {}}
              transition={{ duration: 0.4 }}
              onPointerDown={() => tap(index)}
              role="button"
              tabIndex={-1}
              aria-label={isCurrent ? "Tap here!" : `Step ${index + 1}`}
            >
              {isCurrent && <span className="ch-step-glow" />}
              <motion.span
                style={{ display: "grid", placeItems: "center" }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 15, delay: 0.25 + index * 0.1 }}
              >
                <Sprite
                  name={payload.stepAsset}
                  className="ch-step-sprite"
                  style={{ transform: `rotate(${stepAngle(allPoints, index)}deg)` }}
                />
              </motion.span>
              {!isDone && <span className={`ch-step-number ${isCurrent ? "is-current" : ""}`}>{index + 1}</span>}
              {isDone && <span className="ch-step-check"><CheckIcon size={13} /></span>}
            </motion.div>
          );
        })}

        {payload.goalCharacter && (
          <motion.div
            className="ch-goal-character"
            style={{ left: `${goalPoint.x * 100}%`, top: `${goalPoint.y * 100}%` }}
            animate={{ scale: isComplete ? 1.15 : 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 16 }}
          >
            <CharacterView character={payload.goalCharacter} happy={isComplete} />
          </motion.div>
        )}

        {payload.escort && (
          <motion.div
            className="ch-escort"
            animate={{ left: `${escortUnit.x * 100}%`, top: `${escortUnit.y * 100}%`, rotate: isComplete ? 0 : currentIndex % 2 ? -3 : 3 }}
            transition={{ type: "spring", stiffness: 120, damping: 15 }}
          >
            <CharacterView character={payload.escort} happy={isComplete} />
          </motion.div>
        )}

        {payload.waitForGreenLight && (
          <div className="ch-traffic-light" style={{ left: "14%", top: "16%" }}>
            <Sprite name={lightGreen ? "ch_light_green" : "ch_light_red"} className="ch-light-sprite" />
            <span className={`ch-light-bubble ${lightGreen ? "is-green" : ""}`}>
              {lightGreen ? "Go!" : "Wait for green…"}
            </span>
          </div>
        )}

        <AnimatePresence>
          {isComplete && (
            <motion.div
              className="ch-goal-label"
              style={{ left: `${goalPoint.x * 100}%`, top: `${goalPoint.y * 100}%` }}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {payload.goalLabel}
            </motion.div>
          )}
        </AnimatePresence>

        <SparkleBurst trigger={burst.key} reach={60} style={{ left: burst.left, top: burst.top }} />
      </div>
    </div>
  );
};

export default TapSequence;
